import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  LayoutDashboard, Building2, CreditCard, Palette, Mail, Plus, Trash2, LogIn, Ban, CheckCircle2, TrendingUp, Wallet,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => "£" + Number(n || 0).toFixed(0);
const money2 = (n) => "£" + Number(n || 0).toFixed(2);
const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "revenue", label: "Revenue (MRR)", icon: TrendingUp },
  { key: "businesses", label: "Businesses", icon: Building2 },
  { key: "plans", label: "Plans", icon: CreditCard },
  { key: "payments", label: "Payments", icon: Wallet },
  { key: "branding", label: "Branding", icon: Palette },
  { key: "email", label: "Email templates", icon: Mail },
];

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  return (
    <DashboardShell title="Super admin" nav={NAV} active={tab} onSelect={setTab}>
      {tab === "overview" && <Overview />}
      {tab === "revenue" && <Revenue />}
      {tab === "businesses" && <Businesses />}
      {tab === "plans" && <Plans />}
      {tab === "payments" && <PlatformPayments />}
      {tab === "branding" && <Branding />}
      {tab === "email" && <EmailTemplates />}
    </DashboardShell>
  );
}

function PlatformPayments() {
  const [st, setSt] = useState(null);
  const [form, setForm] = useState({ secret_key: "", publishable_key: "", webhook_secret: "" });
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => api.get("/admin/payment-settings").then((r) => setSt(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.secret_key.trim()) return toast.error("Enter your Stripe secret key");
    setSaving(true);
    try {
      const { data } = await api.put("/admin/payment-settings", form);
      toast.success(`Stripe connected — ${data.account_name}`);
      setForm({ secret_key: "", publishable_key: "", webhook_secret: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Stripe? Businesses won't be able to start paid subscriptions until you reconnect.")) return;
    await api.delete("/admin/payment-settings"); toast.success("Stripe disconnected"); load();
  };

  if (!st) return <p className="text-sm text-slate-400">Loading…</p>;
  return (
    <div className="max-w-2xl" data-testid="platform-payments">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Subscription payments</h1>
      <p className="mt-1 text-sm text-slate-500">Connect <b>your</b> platform Stripe account. This is the account that charges laundries for their LaundryDiary subscriptions.</p>

      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5">
        {st.connected ? (
          <div className="flex items-center justify-between" data-testid="platform-stripe-connected">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-green-100"><CheckCircle2 className="h-5 w-5 text-green-600" /></span>
              <div><p className="font-semibold text-slate-800">Connected</p><p className="text-xs text-slate-500">{st.account_name} · key {st.secret_key_masked}{st.webhook_secret_set ? " · webhook set" : ""}</p></div>
            </div>
            <Button variant="ghost" onClick={disconnect} className="text-red-500 hover:bg-red-50" data-testid="platform-stripe-disconnect">Disconnect</Button>
          </div>
        ) : (
          <div className="flex items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-700" data-testid="platform-stripe-disconnected"><Ban className="h-4 w-4" /> Not connected — businesses can't start paid subscriptions yet.</div>
        )}
        <div className="mt-4 space-y-3">
          <div className="space-y-1.5"><Label>Stripe secret key</Label><Input type="password" value={form.secret_key} onChange={(e) => setForm((f) => ({ ...f, secret_key: e.target.value }))} placeholder={st.connected ? "•••• (enter to replace)" : "sk_live_…"} data-testid="platform-secret-key" /></div>
          <div className="space-y-1.5"><Label>Publishable key <span className="text-slate-400">(optional)</span></Label><Input value={form.publishable_key} onChange={(e) => setForm((f) => ({ ...f, publishable_key: e.target.value }))} placeholder="pk_live_…" data-testid="platform-publishable-key" /></div>
          <div className="space-y-1.5"><Label>Webhook signing secret <span className="text-slate-400">(optional)</span></Label><Input value={form.webhook_secret} onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))} placeholder="whsec_…" data-testid="platform-webhook-secret" /></div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="platform-stripe-save">{saving ? "Verifying…" : "Save & connect"}</Button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}><p className="text-xs font-medium uppercase tracking-wide text-slate-600">{label}</p><p className="mt-1 font-heading text-2xl font-bold text-slate-900">{value}</p></div>;
}

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return null;
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">Platform overview</h1>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Businesses" value={stats.businesses} testid="admin-stat-businesses" />
        <StatCard label="Active subs" value={stats.active_subscriptions} testid="admin-stat-active" />
        <StatCard label="Trialing" value={stats.trialing} testid="admin-stat-trialing" />
        <StatCard label="Clients" value={stats.clients} testid="admin-stat-clients" />
        <StatCard label="Orders" value={stats.orders} testid="admin-stat-orders" />
      </div>
    </div>
  );
}

function MrrChart({ trend }) {
  if (!trend.length) return <p className="text-sm text-slate-400">No data yet.</p>;
  const W = 640, H = 180, P = 34;
  const max = Math.max(1, ...trend.map((t) => t.mrr));
  const x = (i) => P + (i * (W - 2 * P)) / Math.max(1, trend.length - 1);
  const y = (v) => H - P - (v / max) * (H - 2 * P);
  const monLabel = (m) => { const [yy, mm] = m.split("-"); return ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][Number(mm)-1] + " " + yy.slice(2); };
  const pts = trend.map((t, i) => `${x(i)},${y(t.mrr)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#e2e8f0" />
      <polyline fill="none" stroke="#0EA5A4" strokeWidth="2.5" points={pts} />
      {trend.map((t, i) => (
        <g key={t.month}>
          <circle cx={x(i)} cy={y(t.mrr)} r="3.5" fill="#0EA5A4" />
          <text x={x(i)} y={Math.min(y(t.mrr) - 10, H - P - 12)} textAnchor="middle" fontSize="10" fill="#0F172A" fontWeight="600">£{t.mrr.toFixed(0)}</text>
          <text x={x(i)} y={H - P + 16} textAnchor="middle" fontSize="9" fill="#94a3b8">{monLabel(t.month)}</text>
        </g>
      ))}
    </svg>
  );
}

function Revenue() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/mrr").then((r) => setM(r.data)); }, []);
  if (!m) return <div className="py-10 text-sm text-slate-400">Loading…</div>;
  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">Revenue</h1>
      <p className="mt-1 text-sm text-slate-500">Recurring revenue and subscription health across all tenants.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-slate-200 bg-slate-950 p-5 text-white" data-testid="mrr-card">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Monthly recurring revenue</p>
          <p className="mt-1 font-heading text-3xl font-extrabold text-teal-300">{money2(m.mrr)}</p>
          <p className="mt-1 text-xs text-slate-400">ARR {money2(m.arr)}</p>
        </div>
        <StatCard label="Paying subscribers" value={m.paying} testid="mrr-paying" />
        <StatCard label="On trial" value={m.trialing} testid="mrr-trialing" />
        <StatCard label="Past due" value={m.past_due} testid="mrr-pastdue" />
        <StatCard label="Paying share (of all tenants)" value={m.conversion_rate + "%"} testid="mrr-conversion" />
        <StatCard label="Cancelled rate" value={m.churn_rate + "%"} testid="mrr-churn" />
        <StatCard label="Cancelled" value={m.canceled} testid="mrr-cancelled" />
        <StatCard label="Total businesses" value={m.total_businesses} testid="mrr-total" />
      </div>
      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-slate-600">MRR trend (6 months)</h2>
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="mrr-trend-chart">
        <MrrChart trend={m.trend || []} />
      </div>
      <h2 className="mt-8 text-sm font-semibold uppercase tracking-wide text-slate-600">MRR by plan</h2>
      <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="mrr-per-plan">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Subscribers</th><th className="px-4 py-3">MRR</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {m.per_plan.map((p) => (
              <tr key={p.plan}><td className="px-4 py-3 font-medium">{p.plan}</td><td className="px-4 py-3">{p.subscribers}</td><td className="px-4 py-3 font-medium">{money2(p.mrr)}</td></tr>
            ))}
            {!m.per_plan.length && <tr><td colSpan="3" className="px-4 py-8 text-center text-slate-400">No paying subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function Businesses() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ business_name: "", owner_name: "", owner_email: "", owner_password: "", plan_id: "" });
  const load = useCallback(() => {
    api.get("/admin/businesses").then((r) => { setRows(r.data); setLoaded(true); });
    api.get("/admin/plans").then((r) => setPlans(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.business_name || !form.owner_name || !form.owner_email) return toast.error("Business name, owner name and email required");
    try { const { data } = await api.post("/admin/businesses", { ...form, plan_id: form.plan_id || null, origin_url: window.location.origin });
      toast.success(data.set_password_email_sent ? "Business created — owner emailed a login link" : "Business created");
      setOpen(false); setForm({ business_name: "", owner_name: "", owner_email: "", owner_password: "", plan_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => { await api.put(`/admin/businesses/${id}/status`, { status }); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this business and ALL its data?")) return; await api.delete(`/admin/businesses/${id}`); load(); };
  const signInAs = async (id) => {
    try { const { data } = await api.post(`/admin/businesses/${id}/impersonate`); impersonate(data); navigate("/business"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Businesses</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-business-btn"><Plus className="mr-1 h-4 w-4" /> Add business</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create a business tenant</DialogTitle><DialogDescription>Set up a new laundry business and its owner account.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Business name</Label><Input value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} data-testid="biz-create-name" /></div>
              <div className="space-y-1.5"><Label>Owner name</Label><Input value={form.owner_name} onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))} data-testid="biz-create-owner" /></div>
              <div className="space-y-1.5"><Label>Owner email</Label><Input type="email" value={form.owner_email} onChange={(e) => setForm((f) => ({ ...f, owner_email: e.target.value }))} data-testid="biz-create-email" /></div>
              <div className="space-y-1.5"><Label>Password (leave blank to email a set-password link)</Label><Input type="password" value={form.owner_password} onChange={(e) => setForm((f) => ({ ...f, owner_password: e.target.value }))} data-testid="biz-create-password" /></div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="biz-create-save">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="businesses-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr><th className="px-4 py-3">Business</th><th className="px-4 py-3">Owner</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Subscription</th><th className="px-4 py-3">Clients</th><th className="px-4 py-3">Actions</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((b) => (
              <tr key={b.id} data-testid={`business-row-${b.id}`}>
                <td className="px-4 py-3"><p className="font-medium">{b.name}</p>{b.status === "suspended" && <Badge className="border-0 bg-red-100 text-red-700">suspended</Badge>}</td>
                <td className="px-4 py-3 text-slate-500">{b.owner?.name}<br /><span className="text-xs">{b.owner?.email}</span></td>
                <td className="px-4 py-3">{b.plan_name}</td>
                <td className="px-4 py-3"><Badge className={`border-0 ${b.sub_state?.active ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{b.sub_state?.status?.replace("_", " ")}</Badge></td>
                <td className="px-4 py-3">{b.client_count}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => signInAs(b.id)} data-testid={`impersonate-${b.id}`}><LogIn className="mr-1 h-3 w-3" /> Sign in</Button>
                    {b.status === "active"
                      ? <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "suspended")} data-testid={`suspend-${b.id}`}><Ban className="mr-1 h-3 w-3" /> Suspend</Button>
                      : <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "active")} data-testid={`activate-${b.id}`}><CheckCircle2 className="mr-1 h-3 w-3" /> Activate</Button>}
                    <button onClick={() => remove(b.id)} className="text-slate-300 hover:text-red-500" data-testid={`delete-business-${b.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan="6" className="px-4 py-10 text-center text-slate-400">{loaded ? "No businesses yet." : "Loading…"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Plans() {
  const [plans, setPlans] = useState([]);
  const load = useCallback(() => api.get("/admin/plans").then((r) => setPlans(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async (p) => {
    try { await api.put(`/admin/plans/${p.id}`, {
      name: p.name, description: p.description, features: p.features, price_monthly: Number(p.price_monthly),
      price_yearly: Number(p.price_yearly), lookup_key_monthly: p.lookup_key_monthly,
      lookup_key_yearly: p.lookup_key_yearly, limits: p.limits || {}, sort: p.sort || 0 });
      toast.success("Plan saved"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upd = (id, k, v) => setPlans((ps) => ps.map((p) => p.id === id ? { ...p, [k]: v } : p));

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-slate-900">Subscription plans</h1>
      <p className="mt-1 text-sm text-slate-500">Prices are billed through Stripe. Editing the display price here does not change the Stripe price — contact support to adjust Stripe amounts.</p>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        {plans.map((p) => (
          <div key={p.id} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`plan-edit-${p.name.toLowerCase()}`}>
            <Input value={p.name} onChange={(e) => upd(p.id, "name", e.target.value)} className="font-heading font-bold" />
            <Textarea value={p.description} onChange={(e) => upd(p.id, "description", e.target.value)} rows={2} />
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Monthly (£, display)</Label><Input type="number" value={p.price_monthly} onChange={(e) => upd(p.id, "price_monthly", e.target.value)} /></div>
              <div><Label className="text-xs">Yearly (£, display)</Label><Input type="number" value={p.price_yearly} onChange={(e) => upd(p.id, "price_yearly", e.target.value)} /></div>
            </div>
            <Textarea value={(p.features || []).join("\n")} onChange={(e) => upd(p.id, "features", e.target.value.split("\n").filter(Boolean))} rows={4} placeholder="One feature per line" />
            <Button onClick={() => save(p)} className="rounded-full bg-brand hover:bg-brand-hover" data-testid={`plan-save-${p.name.toLowerCase()}`}>Save</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Branding() {
  const { refresh } = useSite();
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const site = s.site;
  const setSite = (k, v) => setS((x) => ({ ...x, site: { ...x.site, [k]: v } }));
  const setSmtp = (k, v) => setS((x) => ({ ...x, smtp: { ...x.smtp, [k]: v } }));

  const saveBranding = async () => { await api.put("/admin/settings", { site: s.site }); await refresh(); toast.success("Branding saved"); };
  const saveSmtp = async () => { await api.put("/admin/settings", { smtp: s.smtp }); toast.success("Email settings saved"); };

  return (
    <div className="max-w-2xl">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Branding</h1>
      <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="space-y-1.5"><Label>Site name</Label><Input value={site.site_name || ""} onChange={(e) => setSite("site_name", e.target.value)} data-testid="brand-name" /></div>
        <div className="space-y-1.5"><Label>Browser tab title</Label><Input value={site.page_title || ""} onChange={(e) => setSite("page_title", e.target.value)} data-testid="brand-title" /></div>
        <div className="space-y-1.5"><Label>Tagline</Label><Input value={site.tagline || ""} onChange={(e) => setSite("tagline", e.target.value)} data-testid="brand-tagline" /></div>
        <div className="space-y-1.5"><Label>Logo URL</Label><Input value={site.logo_url || ""} onChange={(e) => setSite("logo_url", e.target.value)} data-testid="brand-logo" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Primary colour</Label><Input type="color" value={site.primary_color || "#0EA5A4"} onChange={(e) => setSite("primary_color", e.target.value)} data-testid="brand-primary" /></div>
          <div className="space-y-1.5"><Label>Accent colour</Label><Input type="color" value={site.secondary_color || "#F59E0B"} onChange={(e) => setSite("secondary_color", e.target.value)} data-testid="brand-secondary" /></div>
        </div>
        <Button onClick={saveBranding} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="brand-save">Save branding</Button>
      </div>

      <div className="mt-5 space-y-3 rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="font-heading font-bold">Outbound email (SMTP)</h2>
        <p className="text-xs text-slate-400">Used to send client & driver login links and order/invoice notifications.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Host</Label><Input value={s.smtp.host || ""} onChange={(e) => setSmtp("host", e.target.value)} data-testid="smtp-host" /></div>
          <div className="space-y-1.5"><Label>Port</Label><Input value={s.smtp.port || ""} onChange={(e) => setSmtp("port", e.target.value)} data-testid="smtp-port" /></div>
        </div>
        <div className="space-y-1.5"><Label>From email</Label><Input value={s.smtp.from_email || ""} onChange={(e) => setSmtp("from_email", e.target.value)} data-testid="smtp-from" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Username</Label><Input value={s.smtp.username || ""} onChange={(e) => setSmtp("username", e.target.value)} data-testid="smtp-user" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="password" placeholder={s.smtp.password_set ? "•••• (set)" : ""} onChange={(e) => setSmtp("password", e.target.value)} data-testid="smtp-pass" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={!!s.smtp.use_ssl} onChange={(e) => setSmtp("use_ssl", e.target.checked)} /> Use SSL</label>
        <Button onClick={saveSmtp} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="smtp-save">Save email settings</Button>
      </div>
    </div>
  );
}

function EmailTemplates() {
  const [tmpls, setTmpls] = useState(null);
  useEffect(() => { api.get("/admin/email-templates").then((r) => setTmpls(r.data)); }, []);
  if (!tmpls) return null;
  const upd = (key, field, v) => setTmpls((t) => ({ ...t, [key]: { ...t[key], [field]: v } }));
  const save = async () => { await api.put("/admin/email-templates", { templates: tmpls }); toast.success("Templates saved"); };

  return (
    <div className="max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-slate-900">Email templates</h1>
      <p className="mt-1 text-sm text-slate-500">Placeholders like {"{{client_name}}"}, {"{{business_name}}"}, {"{{order_ref}}"}, {"{{link}}"} are filled automatically.</p>
      <div className="mt-5 space-y-4">
        {Object.keys(tmpls).map((key) => (
          <div key={key} className="space-y-2 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`tmpl-${key}`}>
            <p className="font-heading font-bold capitalize">{key.replace(/_/g, " ")}</p>
            <div className="space-y-1.5"><Label className="text-xs">Subject</Label><Input value={tmpls[key].subject} onChange={(e) => upd(key, "subject", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Body</Label><Textarea value={tmpls[key].body} onChange={(e) => upd(key, "body", e.target.value)} rows={4} /></div>
          </div>
        ))}
        <Button onClick={save} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="tmpl-save">Save all templates</Button>
      </div>
    </div>
  );
}
